import { Suspense, lazy, Component, type ReactNode, type ErrorInfo, forwardRef } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TabProvider } from "@/contexts/TabContext";
import { ChatProvider } from "@/contexts/ChatContext";

import { useOfflineQueueProcessor } from "@/hooks/useOfflineQueue";
import { canAccessModule, type PermissionModule } from "@/config/permissions";
import { trackError } from "@/lib/errorTracker";
import { handleSessionError } from "@/lib/sessionErrorHandler";
import { initGlobalErrorCatcher } from "@/lib/bugReporter";
import { initErrorTracking } from "@/lib/errorTracker";
import { CookieConsentBanner } from "@/components/gdpr/CookieConsentBanner";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { NewVersionBanner } from "@/components/NewVersionBanner";
import { BugReportButton } from "@/components/bug-report/BugReportButton";
import { UnifiedChatBubble } from "@/components/chat/UnifiedChatBubble";
import { CompanyGuard } from "@/components/guards/CompanyGuard";
import { IdleWarningDialog } from "@/components/auth/IdleWarningDialog";

import { Loader2, ShieldX } from "lucide-react";

const LoginPage = lazyRetry(() => import("./pages/LoginPage"));
const LandingPage = lazyRetry(() => import("./pages/LandingPage"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const DemoPage = lazyRetry(() => import("./pages/DemoPage"));
const InstallPage = lazyRetry(() => import("./pages/InstallPage"));
const OAuthConsentPage = lazyRetry(() => import("./pages/OAuthConsentPage"));

/** Retry wrapper for lazy imports — handles stale module errors after HMR */
function lazyRetry(factory: () => Promise<any>) {
  return lazy(() =>
    factory().catch((err) => {
      // If the module fetch failed (stale cache), reload the page once
      const key = 'lazy_retry_reload';
      const hasReloaded = sessionStorage.getItem(key);
      if (!hasReloaded) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        return new Promise(() => {}); // never resolves, page reloads
      }
      sessionStorage.removeItem(key);
      throw err;
    })
  );
}

/** Error boundary to catch lazy-load failures gracefully */
class LazyErrorBoundary extends Component<{ children: ReactNode; label?: string }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    try {
      trackError({
        category: 'frontend_js',
        severity: error.message?.includes('call stack') ? 'critical' : 'high',
        title: `LazyLoad [${this.props.label || 'root'}]: ${error.message?.slice(0, 80) || 'unknown'}`,
        message: error.message,
        stack: error.stack,
        source: info.componentStack?.slice(0, 500) || undefined,
      });
    } catch { /* silent */ }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
          <p className="text-sm text-muted-foreground">Siden kunne ikke indlæses.</p>
          <button onClick={() => window.location.reload()} className="text-sm text-primary hover:underline">Genindlæs</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Lazy-loaded pages
const ModeChooserPage = lazyRetry(() => import("./pages/ModeChooserPage"));
const NotificationsPage = lazyRetry(() => import("./pages/NotificationsPage"));
const ProfilePage = lazyRetry(() => import("./pages/ProfilePage"));
const FieldDayPage = lazyRetry(() => import("./pages/FieldDayPage"));
const FieldCalendarPage = lazyRetry(() => import("./pages/FieldCalendarPage"));
const FieldAllTasksPage = lazyRetry(() => import("./pages/FieldAllTasksPage"));
const FieldStatsPage = lazyRetry(() => import("./pages/FieldStatsPage"));
const FieldTaskDetailPage = lazyRetry(() => import("./pages/FieldTaskDetailPage"));
const FieldRoomDocPage = lazyRetry(() => import("./pages/FieldRoomDocPage"));
const FieldShiftsPage = lazyRetry(() => import("./pages/FieldShiftsPage"));
const FieldReceiptHistoryPage = lazyRetry(() => import("./pages/FieldReceiptHistoryPage"));
const FieldCaseWizardPage = lazyRetry(() => import("./pages/FieldCaseWizardPage"));
const FieldFinalReviewListPage = lazyRetry(() => import("./pages/FieldFinalReviewListPage"));
const FieldFinalReviewPage = lazyRetry(() => import("./pages/FieldFinalReviewPage"));
const FieldTimePage = lazyRetry(() => import("./pages/FieldTimePage"));
const MobileSwitchboardPage = lazyRetry(() => import("./pages/MobileSwitchboardPage"));
const OfficeDashboardPage = lazyRetry(() => import("./pages/OfficeDashboardPage"));
const OfficeCaseListPage = lazyRetry(() => import("./pages/OfficeCaseListPage"));
const OfficeRecycleBinPage = lazyRetry(() => import("./pages/OfficeRecycleBinPage"));
const OfficeCaseDetailPage = lazyRetry(() => import("./pages/OfficeCaseDetailPage"));
const CreateProjectPage = lazyRetry(() => import("./pages/CreateProjectPage"));
const QuoteEditorPage = lazyRetry(() => import("./pages/QuoteEditorPage"));
const QuotePreviewPage = lazyRetry(() => import("./pages/QuotePreviewPage"));
const QuoteStudioPage = lazyRetry(() => import("./pages/QuoteStudioPage"));
const InvoiceStudioPage = lazyRetry(() => import("./pages/InvoiceStudioPage"));
const OfficeCustomerListPage = lazyRetry(() => import("./pages/OfficeCustomerListPage"));
const OfficeCustomerDetailPage = lazyRetry(() => import("./pages/OfficeCustomerDetailPage"));
const OfficeCustomerCreatePage = lazyRetry(() => import("./pages/OfficeCustomerCreatePage"));
const OfficeQuoteListPage = lazyRetry(() => import("./pages/OfficeQuoteListPage"));
const OfficeInvoiceListPage = lazyRetry(() => import("./pages/OfficeInvoiceListPage"));
const OfficeAccountingExportPage = lazyRetry(() => import("./pages/OfficeAccountingExportPage"));
const OfficeCardsPage = lazyRetry(() => import("./pages/OfficeCardsPage"));

const OfficeReportsPage = lazyRetry(() => import("./pages/OfficeReportsPage"));
const OfficeSchedulePage = lazyRetry(() => import("./pages/OfficeSchedulePage"));
const OfficeShiftPlanPage = lazyRetry(() => import("./pages/OfficeShiftPlanPage"));
const OfficeReceiptListPage = lazyRetry(() => import("./pages/OfficeReceiptListPage"));
const OfficeReceiptDetailPage = lazyRetry(() => import("./pages/OfficeReceiptDetailPage"));
const OfficePayrollPage = lazyRetry(() => import("./pages/OfficePayrollPage"));
const OfficeUserManagementPage = lazyRetry(() => import("./pages/OfficeUserManagementPage"));
const OfficeEmployeeDetailPage = lazyRetry(() => import("./pages/OfficeEmployeeDetailPage"));
const OfficeMileagePage = lazyRetry(() => import("./pages/OfficeMileagePage"));
const OfficeBugReportsPage = lazyRetry(() => import("./pages/OfficeBugReportsPage"));
const OfficeSystemHealthPage = lazyRetry(() => import("./pages/OfficeSystemHealthPage"));
const OfficeEmailLogPage = lazyRetry(() => import("./pages/OfficeEmailLogPage"));
const OfficeGdprPage = lazyRetry(() => import("./pages/OfficeGdprPage"));
const OfficeMessageTemplatesPage = lazyRetry(() => import("./pages/OfficeMessageTemplatesPage"));
const OfficeTimeTrackingPage = lazyRetry(() => import("./pages/OfficeTimeTrackingPage"));
const OfficeCustomerSatisfactionPage = lazyRetry(() => import("./pages/OfficeCustomerSatisfactionPage"));
const OfficeRemindersPage = lazyRetry(() => import("./pages/OfficeRemindersPage"));
const OfficePriceListPage = lazyRetry(() => import("./pages/OfficePriceListPage"));
const OfficeDialerPage = lazyRetry(() => import("./pages/OfficeDialerPage"));
const OfficeSwitchboardPage = lazyRetry(() => import("./pages/OfficeSwitchboardPage"));
const OfficeTwilioEventsPage = lazyRetry(() => import("./pages/OfficeTwilioEventsPage"));
const OfficeStrategyBoardPage = lazyRetry(() => import("./pages/OfficeStrategyBoardPage"));
const OfficeWarehousePage = lazyRetry(() => import("./pages/OfficeWarehousePage"));
const OfficeWebsiteChatPage = lazyRetry(() => import("./pages/OfficeWebsiteChatPage"));
const OfficeLiveSessionsPage = lazyRetry(() => import("./pages/OfficeLiveSessionsPage"));
const OfficeCompanySetupPage = lazyRetry(() => import("./pages/OfficeCompanySetupPage"));
const OfficeLeadWebhookPage = lazyRetry(() => import("./pages/OfficeLeadWebhookPage"));
const OfficeFeatureFlagsPage = lazyRetry(() => import("./pages/OfficeFeatureFlagsPage"));
const OfficeTenantDirectoryPage = lazyRetry(() => import("./pages/OfficeTenantDirectoryPage"));
const OfficeTradePricingPage = lazyRetry(() => import("./pages/OfficeTradePricingPage"));
const OfficeCampaignsPage = lazyRetry(() => import("./pages/OfficeCampaignsPage"));
const OfficeCampaignEditorPage = lazyRetry(() => import("./pages/OfficeCampaignEditorPage"));


// Webshop pages
const WebshopDashboardPage = lazyRetry(() => import("./pages/webshop/WebshopDashboardPage"));
const WebshopOrdersPage = lazyRetry(() => import("./pages/webshop/WebshopOrdersPage"));
const WebshopProductsPage = lazyRetry(() => import("./pages/webshop/WebshopProductsPage"));
const WebshopShippingPage = lazyRetry(() => import("./pages/webshop/WebshopShippingPage"));
const WebshopMessagesPage = lazyRetry(() => import("./pages/webshop/WebshopMessagesPage"));
const WebshopReviewsPage = lazyRetry(() => import("./pages/webshop/WebshopReviewsPage"));
const WebshopDiscountCodesPage = lazyRetry(() => import("./pages/webshop/WebshopDiscountCodesPage"));
const WebshopSalesDashboardPage = lazyRetry(() => import("./pages/webshop/WebshopSalesDashboardPage"));
const WebshopReturnsPage = lazyRetry(() => import("./pages/webshop/WebshopReturnsPage"));
const WebshopEmailFlowsPage = lazyRetry(() => import("./pages/webshop/WebshopEmailFlowsPage"));
const OnboardingPage = lazyRetry(() => import("./pages/OnboardingPage"));
const FeedbackPage = lazyRetry(() => import("./pages/FeedbackPage"));
const PublicSharePage = lazyRetry(() => import("./pages/PublicSharePage"));
const ResetPasswordPage = lazyRetry(() => import("./pages/ResetPasswordPage"));
const PortalLoginPage = lazyRetry(() => import("./pages/portal/PortalLoginPage"));
const PortalVerifyPage = lazyRetry(() => import("./pages/portal/PortalVerifyPage"));
const PortalDashboardPage = lazyRetry(() => import("./pages/portal/PortalDashboardPage"));
const PortalQuotesPage = lazyRetry(() => import("./pages/portal/PortalQuotesPage"));
const PortalInvoicesPage = lazyRetry(() => import("./pages/portal/PortalInvoicesPage"));
const PortalInvoiceDetailPage = lazyRetry(() => import("./pages/portal/PortalInvoiceDetailPage"));
const PortalMessagesPage = lazyRetry(() => import("./pages/portal/PortalMessagesPage"));
const PortalDocumentsPage = lazyRetry(() => import("./pages/portal/PortalDocumentsPage"));
const PortalExtraWorkPage = lazyRetry(() => import("./pages/portal/PortalExtraWorkPage"));
const PortalBookingPage = lazyRetry(() => import("./pages/portal/PortalBookingPage"));
const PortalRedirectPage = lazyRetry(() => import("./pages/portal/PortalRedirectPage"));
const ShortLinkRedirectPage = lazyRetry(() => import("./pages/ShortLinkRedirectPage"));
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on auth errors
        if (error?.status === 401 || error?.status === 403) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      onError: (error: any) => {
        handleSessionError(error);
      },
    },
  },
});

/** Roles that can choose between office and field mode */
const DUAL_MODE_ROLES = ['admin', 'supervisor', 'founder'];

/** Get the current app mode from sessionStorage */
function getAppMode(): 'office' | 'field' | 'webshop' | null {
  const mode = sessionStorage.getItem('app_mode');
  if (mode === 'office' || mode === 'field' || mode === 'webshop') return mode;
  return null;
}

const ProtectedRoute = forwardRef<HTMLDivElement, {
  children: React.ReactNode;
  allowedSystemRoles?: string[];
  requiredModule?: PermissionModule;
  platformOwnerOnly?: boolean;
}>(function ProtectedRoute({ children, allowedSystemRoles, requiredModule, platformOwnerOnly }, _ref) {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // Block users without a company association (prevents crash from company_id! assertions)
  if (!user?.company_id) {
    return <CompanyGuard>{children}</CompanyGuard>;
  }

  // Platform owner only routes
  if (platformOwnerOnly && !user?.isPlatformOwner) {
    return <NoAccessPage />;
  }

  const appMode = getAppMode();
  const isDualRole = user?.systemRole && DUAL_MODE_ROLES.includes(user.systemRole);

  // Check explicit role list
  if (allowedSystemRoles && user?.systemRole) {
    const hasDirectAccess = allowedSystemRoles.includes(user.systemRole);
    // Allow dual-role users in field mode to access field_worker routes
    const isFieldRoute = allowedSystemRoles.includes('field_worker');
    const hasFieldModeAccess = isDualRole && appMode === 'field' && isFieldRoute;

    if (!hasDirectAccess && !hasFieldModeAccess) {
      if (user.systemRole === 'field_worker') return <Navigate to="/field" replace />;
      return <NoAccessPage />;
    }
  }

  // Check module permission
  if (requiredModule && !canAccessModule(user?.systemRole, requiredModule)) {
    if (user?.systemRole === 'field_worker') return <Navigate to="/field" replace />;
    return <NoAccessPage />;
  }

  return <>{children}</>;
});

function NoAccessPage() {
  const appMode = getAppMode();
  const dashboardPath = appMode === 'webshop' ? '/webshop' : appMode === 'field' ? '/field' : '/office';
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
      <ShieldX className="w-12 h-12 text-muted-foreground" />
      <h1 className="text-xl font-semibold">Ingen adgang</h1>
      <p className="text-sm text-muted-foreground max-w-sm text-center">
        Din rolle har ikke adgang til denne side. Kontakt en administrator hvis du mener, dette er en fejl.
      </p>
      <a href={dashboardPath} className="text-sm text-primary hover:underline">Gå til Dashboard</a>
    </div>
  );
}

function safeSameOriginRelative(path: string | null): string | null {
  if (!path) return null;
  if (!path.startsWith("/") || path.startsWith("//")) return null;
  return path;
}

function PostLoginRedirect() {
  const next = safeSameOriginRelative(new URLSearchParams(window.location.search).get("next"));
  if (next) return <Navigate to={next} replace />;
  return <RoleRedirect />;
}

function RoleRedirect() {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  // Pure field workers always go to field
  if (user.systemRole === 'field_worker') return <Navigate to="/field" replace />;

  // Dual-role users: check if they've already chosen a mode
  const isDualRole = user.systemRole && DUAL_MODE_ROLES.includes(user.systemRole);
  if (isDualRole) {
    const appMode = getAppMode();
    if (appMode === 'field') return <Navigate to="/field" replace />;
    if (appMode === 'office') return <Navigate to="/office" replace />;
    if (appMode === 'webshop') return <Navigate to="/webshop" replace />;
    // No mode chosen yet — show chooser
    return <Navigate to="/choose-mode" replace />;
  }

  // Other office roles go straight to office
  return <Navigate to="/office" replace />;
}

function OfflineQueueMount() {
  useOfflineQueueProcessor();
  return null;
}

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <LazyErrorBoundary>
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}>
      <Routes>
        {/* Auth */}
        <Route path="/login" element={isAuthenticated ? <PostLoginRedirect /> : <LoginPage />} />
        <Route path="/.lovable/oauth/consent" element={<OAuthConsentPage />} />
        <Route path="/homie" element={<Navigate to="/login" replace />} />
        <Route path="/log-ind" element={<Navigate to="/login" replace />} />
        <Route path="/logind" element={<Navigate to="/login" replace />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/choose-mode" element={<ProtectedRoute><ModeChooserPage /></ProtectedRoute>} />

        {/* Field worker */}
        <Route path="/field" element={<ProtectedRoute allowedSystemRoles={['field_worker']}><FieldDayPage /></ProtectedRoute>} />
        <Route path="/field/calendar" element={<ProtectedRoute allowedSystemRoles={['field_worker']}><FieldCalendarPage /></ProtectedRoute>} />
        <Route path="/field/tasks" element={<ProtectedRoute allowedSystemRoles={['field_worker']}><FieldAllTasksPage /></ProtectedRoute>} />
        <Route path="/field/tasks/:taskId" element={<ProtectedRoute allowedSystemRoles={['field_worker']}><FieldTaskDetailPage /></ProtectedRoute>} />
        <Route path="/field/tasks/:taskId/rooms/:roomId" element={<ProtectedRoute allowedSystemRoles={['field_worker']}><FieldRoomDocPage /></ProtectedRoute>} />
        <Route path="/field/shifts" element={<ProtectedRoute allowedSystemRoles={['field_worker']}><FieldShiftsPage /></ProtectedRoute>} />
        <Route path="/field/receipts" element={<ProtectedRoute allowedSystemRoles={['field_worker']}><FieldReceiptHistoryPage /></ProtectedRoute>} />
        <Route path="/field/case-wizard/:projectId" element={<ProtectedRoute allowedSystemRoles={['field_worker']}><FieldCaseWizardPage /></ProtectedRoute>} />
        <Route path="/field/tasks/:taskId/final-review" element={<ProtectedRoute allowedSystemRoles={['field_worker']}><FieldFinalReviewListPage /></ProtectedRoute>} />
        <Route path="/field/tasks/:taskId/final-review/:roomId" element={<ProtectedRoute allowedSystemRoles={['field_worker']}><FieldFinalReviewPage /></ProtectedRoute>} />
        <Route path="/field/time" element={<ProtectedRoute allowedSystemRoles={['field_worker']}><FieldTimePage /></ProtectedRoute>} />
        <Route path="/field/stats" element={<ProtectedRoute allowedSystemRoles={['field_worker']}><FieldStatsPage /></ProtectedRoute>} />
        <Route path="/field/switchboard" element={<ProtectedRoute allowedSystemRoles={['field_worker']}><MobileSwitchboardPage /></ProtectedRoute>} />

        {/* Office / Admin — permission-gated */}
        <Route path="/office" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'backoffice', 'accounting', 'auditor', 'founder']}><OfficeDashboardPage /></ProtectedRoute>} />
        <Route path="/office/cases" element={<ProtectedRoute requiredModule="cases"><OfficeCaseListPage /></ProtectedRoute>} />
        <Route path="/office/recycle-bin" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'backoffice', 'accounting', 'founder']}><OfficeRecycleBinPage /></ProtectedRoute>} />
        <Route path="/office/cases/new" element={<ProtectedRoute requiredModule="cases"><CreateProjectPage /></ProtectedRoute>} />
        <Route path="/office/cases/:id/edit" element={<ProtectedRoute requiredModule="cases"><CreateProjectPage /></ProtectedRoute>} />
        <Route path="/office/cases/:id" element={<ProtectedRoute requiredModule="cases"><OfficeCaseDetailPage /></ProtectedRoute>} />
        <Route path="/office/cases/:id/quote/new" element={<ProtectedRoute requiredModule="quotes"><QuoteEditorPage /></ProtectedRoute>} />
        <Route path="/office/cases/:id/quote/:quoteId/preview" element={<ProtectedRoute requiredModule="quotes"><QuotePreviewPage /></ProtectedRoute>} />
        <Route path="/office/cases/:id/quote/:quoteId/studio" element={<ProtectedRoute requiredModule="quotes"><QuoteStudioPage /></ProtectedRoute>} />
        <Route path="/office/cases/:id/invoice/:invoiceId/studio" element={<ProtectedRoute requiredModule="invoices"><InvoiceStudioPage /></ProtectedRoute>} />
        <Route path="/office/customers" element={<ProtectedRoute requiredModule="customers"><OfficeCustomerListPage /></ProtectedRoute>} />
        <Route path="/office/customers/new" element={<ProtectedRoute requiredModule="customers"><OfficeCustomerCreatePage /></ProtectedRoute>} />
        <Route path="/office/customers/:id" element={<ProtectedRoute requiredModule="customers"><OfficeCustomerDetailPage /></ProtectedRoute>} />
        <Route path="/office/quotes" element={<ProtectedRoute requiredModule="quotes"><OfficeQuoteListPage /></ProtectedRoute>} />
        <Route path="/office/invoices" element={<ProtectedRoute requiredModule="invoices"><OfficeInvoiceListPage /></ProtectedRoute>} />
        <Route path="/office/accounting-export" element={<ProtectedRoute requiredModule="accounting"><OfficeAccountingExportPage /></ProtectedRoute>} />
        <Route path="/office/cards" element={<ProtectedRoute requiredModule="invoices"><OfficeCardsPage /></ProtectedRoute>} />
                        
        <Route path="/office/reports" element={<ProtectedRoute requiredModule="reports"><OfficeReportsPage /></ProtectedRoute>} />
        <Route path="/office/schedule" element={<ProtectedRoute requiredModule="schedule"><OfficeSchedulePage /></ProtectedRoute>} />
        <Route path="/office/shift-plan" element={<ProtectedRoute requiredModule="shift_plan"><OfficeShiftPlanPage /></ProtectedRoute>} />
        <Route path="/office/receipts" element={<ProtectedRoute requiredModule="receipts"><OfficeReceiptListPage /></ProtectedRoute>} />
        <Route path="/office/receipts/:id" element={<ProtectedRoute requiredModule="receipts"><OfficeReceiptDetailPage /></ProtectedRoute>} />
        <Route path="/office/payroll" element={<ProtectedRoute requiredModule="payroll"><OfficePayrollPage /></ProtectedRoute>} />
       <Route path="/office/users" element={<ProtectedRoute requiredModule="users"><OfficeUserManagementPage /></ProtectedRoute>} />
       
        <Route path="/office/users/:userId" element={<ProtectedRoute requiredModule="users"><OfficeEmployeeDetailPage /></ProtectedRoute>} />
        <Route path="/office/mileage" element={<ProtectedRoute requiredModule="mileage"><OfficeMileagePage /></ProtectedRoute>} />
        <Route path="/office/bug-reports" element={<ProtectedRoute requiredModule="bug_reports"><OfficeBugReportsPage /></ProtectedRoute>} />
         <Route path="/office/system-health" element={<ProtectedRoute allowedSystemRoles={['admin', 'founder']}><OfficeSystemHealthPage /></ProtectedRoute>} />
         <Route path="/office/email-log" element={<ProtectedRoute allowedSystemRoles={['admin', 'founder']}><OfficeEmailLogPage /></ProtectedRoute>} />
        <Route path="/office/time-tracking" element={<ProtectedRoute requiredModule="payroll"><OfficeTimeTrackingPage /></ProtectedRoute>} />
        <Route path="/office/customer-satisfaction" element={<ProtectedRoute requiredModule="reports"><OfficeCustomerSatisfactionPage /></ProtectedRoute>} />
        <Route path="/office/reminders" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'founder']}><OfficeRemindersPage /></ProtectedRoute>} />
        <Route path="/office/kampagner" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'founder', 'backoffice']}><OfficeCampaignsPage /></ProtectedRoute>} />
        <Route path="/office/kampagner/:id" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'founder', 'backoffice']}><OfficeCampaignEditorPage /></ProtectedRoute>} />
        <Route path="/office/price-list" element={<ProtectedRoute requiredModule="cases"><OfficePriceListPage /></ProtectedRoute>} />
        <Route path="/office/trade-pricing" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'founder']}><OfficeTradePricingPage /></ProtectedRoute>} />
        <Route path="/office/dialer" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'founder', 'backoffice']}><OfficeDialerPage /></ProtectedRoute>} />
        <Route path="/office/switchboard" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'founder', 'backoffice']}><OfficeSwitchboardPage /></ProtectedRoute>} />
        <Route path="/office/twilio-events" element={<ProtectedRoute allowedSystemRoles={['admin', 'founder']}><OfficeTwilioEventsPage /></ProtectedRoute>} />
        <Route path="/office/strategy" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'founder']}><OfficeStrategyBoardPage /></ProtectedRoute>} />
        <Route path="/office/warehouse" element={<ProtectedRoute requiredModule="warehouse"><OfficeWarehousePage /></ProtectedRoute>} />
        <Route path="/office/website-chat" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'founder', 'backoffice']}><OfficeWebsiteChatPage /></ProtectedRoute>} />
        <Route path="/office/live-sessions" element={<ProtectedRoute platformOwnerOnly><OfficeLiveSessionsPage /></ProtectedRoute>} />
        <Route path="/office/feature-flags" element={<ProtectedRoute platformOwnerOnly><OfficeFeatureFlagsPage /></ProtectedRoute>} />
        <Route path="/office/tenant-directory" element={<ProtectedRoute platformOwnerOnly><OfficeTenantDirectoryPage /></ProtectedRoute>} />
        <Route path="/office/company-setup" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'founder']}><OfficeCompanySetupPage /></ProtectedRoute>} />
        <Route path="/office/lead-webhook" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'founder']}><OfficeLeadWebhookPage /></ProtectedRoute>} />

        {/* Webshop routes */}
        <Route path="/webshop" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'backoffice', 'accounting', 'auditor', 'founder']}><WebshopDashboardPage /></ProtectedRoute>} />
        <Route path="/webshop/orders" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'backoffice', 'founder']}><WebshopOrdersPage /></ProtectedRoute>} />
        <Route path="/webshop/products" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'backoffice', 'founder']}><WebshopProductsPage /></ProtectedRoute>} />
        <Route path="/webshop/customers" element={<ProtectedRoute requiredModule="customers"><OfficeCustomerListPage /></ProtectedRoute>} />
        <Route path="/webshop/customers/new" element={<ProtectedRoute requiredModule="customers"><OfficeCustomerCreatePage /></ProtectedRoute>} />
        <Route path="/webshop/customers/:id" element={<ProtectedRoute requiredModule="customers"><OfficeCustomerDetailPage /></ProtectedRoute>} />
        <Route path="/webshop/warehouse" element={<ProtectedRoute requiredModule="warehouse"><OfficeWarehousePage /></ProtectedRoute>} />
        <Route path="/webshop/shipping" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'backoffice', 'founder']}><WebshopShippingPage /></ProtectedRoute>} />
        <Route path="/webshop/invoices" element={<ProtectedRoute requiredModule="invoices"><OfficeInvoiceListPage /></ProtectedRoute>} />
        <Route path="/webshop/receipts" element={<ProtectedRoute requiredModule="receipts"><OfficeReceiptListPage /></ProtectedRoute>} />
        <Route path="/webshop/receipts/:id" element={<ProtectedRoute requiredModule="receipts"><OfficeReceiptDetailPage /></ProtectedRoute>} />
        <Route path="/webshop/accounting" element={<ProtectedRoute requiredModule="accounting"><OfficeAccountingExportPage /></ProtectedRoute>} />
        <Route path="/webshop/reports" element={<ProtectedRoute requiredModule="reports"><OfficeReportsPage /></ProtectedRoute>} />
        <Route path="/webshop/messages" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'backoffice', 'founder']}><WebshopMessagesPage /></ProtectedRoute>} />
        <Route path="/webshop/templates" element={<Navigate to="/office/message-templates" replace />} />
        <Route path="/webshop/reviews" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'backoffice', 'founder']}><WebshopReviewsPage /></ProtectedRoute>} />
        <Route path="/webshop/discount-codes" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'founder']}><WebshopDiscountCodesPage /></ProtectedRoute>} />
        <Route path="/webshop/sales-dashboard" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'founder']}><WebshopSalesDashboardPage /></ProtectedRoute>} />
        <Route path="/webshop/returns" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'backoffice', 'founder']}><WebshopReturnsPage /></ProtectedRoute>} />
        <Route path="/webshop/email-flows" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'founder']}><WebshopEmailFlowsPage /></ProtectedRoute>} />
        <Route path="/webshop/price-list" element={<ProtectedRoute requiredModule="cases"><OfficePriceListPage /></ProtectedRoute>} />
        <Route path="/webshop/users" element={<ProtectedRoute requiredModule="users"><OfficeUserManagementPage /></ProtectedRoute>} />
        <Route path="/webshop/users/:userId" element={<ProtectedRoute requiredModule="users"><OfficeEmployeeDetailPage /></ProtectedRoute>} />
        <Route path="/webshop/bug-reports" element={<ProtectedRoute requiredModule="bug_reports"><OfficeBugReportsPage /></ProtectedRoute>} />
        <Route path="/webshop/system-health" element={<ProtectedRoute allowedSystemRoles={['admin', 'founder']}><OfficeSystemHealthPage /></ProtectedRoute>} />
        <Route path="/office/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
        <Route path="/office/gdpr" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'founder']}><OfficeGdprPage /></ProtectedRoute>} />
        <Route path="/office/message-templates" element={<ProtectedRoute allowedSystemRoles={['admin', 'supervisor', 'founder']}><OfficeMessageTemplatesPage /></ProtectedRoute>} />

        {/* Password reset (no auth guard — Supabase provides session via link) */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Public share (no auth) */}
        <Route path="/share/:token" element={<PublicSharePage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/l/:code" element={<ShortLinkRedirectPage />} />

        {/* Customer Portal (no auth — uses own token-based session) */}
        <Route path="/portal/go" element={<PortalRedirectPage />} />
        <Route path="/portal/login" element={<PortalLoginPage />} />
        <Route path="/portal/verify" element={<PortalVerifyPage />} />
        <Route path="/portal" element={<PortalDashboardPage />} />
        <Route path="/portal/quotes" element={<PortalQuotesPage />} />
        <Route path="/portal/invoices" element={<PortalInvoicesPage />} />
        <Route path="/portal/invoices/detail" element={<PortalInvoiceDetailPage />} />
        <Route path="/portal/messages" element={<PortalMessagesPage />} />
        <Route path="/portal/documents" element={<PortalDocumentsPage />} />
        <Route path="/portal/extra-work" element={<PortalExtraWorkPage />} />
        <Route path="/portal/booking" element={<PortalBookingPage />} />

        {/* Shared */}
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

        {/* Legacy redirects */}
        <Route path="/dashboard" element={<RoleRedirect />} />
        <Route path="/projects" element={<Navigate to="/office/cases" replace />} />
        <Route path="/projects/:id" element={<Navigate to="/office/cases" replace />} />

        <Route path="/install" element={<InstallPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    </LazyErrorBoundary>
  );
}

// Initialize global error catcher once
initGlobalErrorCatcher();
initErrorTracking();

// App root with all providers
import { ThemeProvider } from 'next-themes';

import { TourProvider } from '@/contexts/TourContext';
import { TourOverlay } from '@/components/tour/TourOverlay';
import { SoftphoneProvider } from '@/contexts/SoftphoneContext';
const HIDDEN_CHAT_PATHS = ['/login', '/landing', '/demo'];
const AuthenticatedChatBubble = () => {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return null;
  const path = location.pathname;
  if (HIDDEN_CHAT_PATHS.includes(path) || path.startsWith('/portal')) return null;
  return <UnifiedChatBubble mode="office" />;
};

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} themes={['light', 'dark', 'company']}>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AuthProvider>
        <SoftphoneProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <TabProvider>
            <ChatProvider>
              <TourProvider>
                <NewVersionBanner />
                <OfflineQueueMount />
                <AppRoutes />
                <IdleWarningDialog />
                
                <AuthenticatedChatBubble />
                
                <CookieConsentBanner />
                <InstallPrompt />
                <TourOverlay />
              </TourProvider>
            </ChatProvider>
          </TabProvider>
        </BrowserRouter>
        </SoftphoneProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
  </ThemeProvider>
);

export default App;
